import { requiredValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import { RelationshipConfig } from "config/config";
import { executionLocationField } from "config/resources/sharedFieldsTyped";
import {
    DeleteListAction,
    deleteActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { isYamlEqual, parseYaml, serializeToPrettyYaml } from "utils/valueTransformers";
import { isEnabledField } from "./sharedFields";

const pluginActionVariables = `
    $parent_plugin_action_id: String,
    $record_type: RecordType,
    $name: String,
    $description: String,
    $icon: String,
    $color: String,
    $is_enabled: Boolean,
    $flow_id: String,
    $record_filter: SchemalessData
    $execution_location: String
`;
const pluginActionQuery = `
    parent_plugin_action_id: $parent_plugin_action_id,
    record_type: $record_type,
    name: $name,
    description: $description,
    icon: $icon,
    color: $color,
    is_enabled: $is_enabled,
    flow_id: $flow_id,
    record_filter: $record_filter
    execution_location: $execution_location
`;
const config: RelationshipConfig = {
    label: "Plugin action",
    icon: IconCollection.cubeSend,
    resourceName: "plugin_action",
    resourceClass: "relationship",
    parentResourceName: "plugin",
    parentReferenceField: "plugin_id",
    parentReferenceName: "plugin_id_plugin.name",
    layout: [],
    hideCreateNew: true,
    labelDocs: {
        title: "Plugin action",
        urlPath: "/docs/plugins#configuration",
    },
    actions: {
        shown: [],
        hidden: [deleteActionSignature],
    },
    listActions: {
        shown: [],
        hidden: [{ key: "delete", action: DeleteListAction }],
    },
    graphql: {
        resource: "plugin_action",
        resourceBody: `
            id
            plugin_id
            plugin_id_plugin{
                name
            }
            parent_plugin_action_id
            record_type
            name
            description
            icon
            color
            is_enabled
            flow_id
            type
            record_filter
            execution_location
        `,
        resourceBodyListing: `
            id
            plugin_id
            record_type
            name
        `,
        createVariables: `
            $plugin_id: String
            $type: PluginActionType,
            ${pluginActionVariables}
        `,
        createQuery: `
            plugin_id: $plugin_id
            type: $type,
            ${pluginActionQuery}
        `,
        updateVariables: `
            $id: String!,
            $by: String,
            ${pluginActionVariables}
        `,
        updateQuery: `
            ${pluginActionQuery}
        `,
    },
    form: [
        {
            name: "name",
            component: "text",
            fieldProps: {
                label: "Name",
            },
            fieldAttr: {
                required: true,
            },
            validation: requiredValidator,
        },
        {
            name: "description",
            component: "text",
            fieldProps: {
                label: "Description",
            },
        },
        executionLocationField({
            optionList: [
                { value: "triggering_record", label: "Triggering record" },
                { value: "plugin_resource", label: "Plugin" },
            ],
        }),
        {
            name: "parent_plugin_action_id",
            component: "autocomplete",
            fieldProps: {
                label: "Parent action",
                options: {
                    resource: "plugin_action",
                },
            },
        },
        {
            name: "record_type",
            component: "autocomplete",
            fieldProps: {
                label: "Record type",
                options: {
                    enumName: "RecordType",
                },
            },
        },
        {
            name: "color",
            component: "text",
            fieldProps: {
                label: "Color",
            },
        },
        {
            name: "icon",
            component: "autocomplete",
            fieldProps: {
                label: "Icon",
                options: {
                    list: Object.keys(IconCollection).map((key) => ({
                        label: key,
                        value: key,
                        iconName: key,
                    })),
                },
            },
        },
        isEnabledField,
        {
            name: "flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "Flow",
                options: {
                    resource: "flow",
                },
            },
        },
        {
            name: "record_filter",
            component: "codeEditor",
            fieldProps: {
                label: "Record filter",
                lang: "yaml",
                isReadOnly: false,
                // Editable JSON⇄YAML display-mode toggle. Submitted via
                // parseYaml/isYamlEqual (both parse JSON, a YAML subset), so
                // switching the representation never marks the form dirty.
                structuredDataEdit: true,
            },
            submitDto: (recordFilter: any) => parseYaml(recordFilter),
            isEqual: isYamlEqual,
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const record_filter = serializeToPrettyYaml(data?.record_filter);
            const execution_location =
                data?.execution_location === "triggering_resource"
                    ? "triggering_record"
                    : data?.execution_location;
            return { ...data, record_filter, execution_location };
        },
    },
};
export default config;
